import { Box, Typography, useTheme } from '@mui/material'
import React, { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import AccordionCommon from 'src/components/AccordionCommon'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { EmptyStatusCard } from 'src/components/mooring/EmptyStatusCard'
import { FlexSpinner } from 'src/components/Spinner'
import { MaintenanceViewMode } from 'src/enums'
import { ICagePosition } from 'src/generated-types'
import { IMainComponent } from 'src/generated-types/main-component'
import { IMainComponentType } from 'src/generated-types/main-component-type'
import { ScheduledMaintenanceFilterParams } from 'src/generated-types/scheduled-maintenance-filter'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { MUIThemeColors } from 'src/mui-theme'
import { Flex } from 'src/styles/flexComponent'
import { getThemeColor, getTranslatedText } from 'src/utils'
import { getCageName, getMooringName } from 'src/utils/function'
import NoMaintenance from '../../../assets/svg/no-maintenances.svg'
import { ScheduledMaintenancePerformDialog } from './ScheduledMaintenancePerformDialog'
import ScheduledMaintenanceTableRenderComponent from './ScheduledMaintenanceTableRenderComponent'

interface ScheduledMaintenanceGroupedListProps {
  dataList?: ICagePosition[] | IMainComponentType[] | undefined
  isLoading: boolean
  viewMode: MaintenanceViewMode
  onlyDaily: boolean
  filterIds?: ScheduledMaintenanceFilterParams
}

const ScheduledMaintenanceGroupedList: FC<ScheduledMaintenanceGroupedListProps> = ({
  dataList,
  isLoading,
  viewMode,
  onlyDaily,
  filterIds,
}) => {
  const { t } = useTranslation(['maintenance'])
  const [selectedItem, setSelectedItem] = useState<number>()
  const { customerId, localityId } = useParams()
  const { showDialog } = useDialogCtx()
  const theme = useTheme()

  const onPerformAll = async (getMainComponentType: () => IMainComponent) => {
    const mainComponentType = getMainComponentType()

    const added = await showDialog(
      ScheduledMaintenancePerformDialog,
      {
        title: t('are-you-sure-to-perform', { ns: 'maintenance' }),
        opts: {
          maxWidth: 'sm',
          fullWidth: false,
        },
        componentProps: {
          customerId: customerId as EntityId,
          localityId: localityId as EntityId,
          mainComponentType: mainComponentType,
          isPerformAll: true,
          filterIds: filterIds,
          onlyDaily: onlyDaily,
        },
      },
      undefined,
      false,
    )
    if (!added) return
  }

  const onPerformAllCage = async (cagePosition: ICagePosition) => {
    const added = await showDialog(
      ScheduledMaintenancePerformDialog,
      {
        title: t('are-you-sure-to-perform', { ns: 'maintenance' }),
        opts: {
          maxWidth: 'sm',
          fullWidth: false,
        },
        componentProps: {
          customerId: customerId as EntityId,
          localityId: localityId as EntityId,
          isPerformAll: true,
          onlyDaily: onlyDaily,
          cagePosition: cagePosition,
          filterIds: filterIds,
        },
      },
      undefined,
      false,
    )
    if (!added) return
  }

  return (
    <Box
      sx={{
        overflow: 'auto',
        flexGrow: 1,
        height: 'calc(100% - 50px)',
        paddingBottom: 2,
        minHeight: '80px',
      }}
    >
      {isLoading ? (
        <FlexSpinner />
      ) : dataList && dataList?.length > 0 ? (
        <>
          {dataList?.map(item => (
            <AccordionCommon
              key={item.id}
              iconColor={MUIThemeColors.primaryMain}
              onChange={() => setSelectedItem(selectedItem === item.id ? null : item.id)}
              title={
                <Flex.Row gap={2} justifyContent="space-between" alignItems="center" width="100%">
                  <Typography
                    variant="subtitle2"
                    fontWeight={500}
                    color={getThemeColor(theme, MUIThemeColors.black)}
                    paddingY={'10px'}
                    paddingX={'8px'}
                  >
                    {viewMode === MaintenanceViewMode.GroupByCage
                      ? `${getCageName(item.name, item.reference)}  (${getMooringName(item.mooringName)})`
                      : getTranslatedText(item.name)}
                  </Typography>
                  {(viewMode === MaintenanceViewMode.GroupByEquipment ||
                    viewMode === MaintenanceViewMode.GroupByCage) &&
                    item.hasPerformableMaintenances && (
                      <GridActionButton
                        key="perform-all"
                        variant="contained"
                        buttonText={t('perform-all', { ns: 'maintenance' })}
                        buttonTextProps={{
                          variant: 'subtitle2',
                          fontWeight: 340,
                          fontSize: '12px',
                          color: theme.palette.white[theme.palette.mode],
                        }}
                        bgColor={theme.palette.primaryMain[theme.palette.mode]}
                        shadowWeight={0}
                        height="28px"
                        border={0}
                        onClick={e => {
                          e.stopPropagation()
                          if (viewMode === MaintenanceViewMode.GroupByEquipment) {
                            onPerformAll(() => item)
                          } else if (viewMode === MaintenanceViewMode.GroupByCage) {
                            onPerformAllCage(item)
                          }
                        }}
                        sx={{ boxShadow: 'none', minWidth: '112px' }}
                      />
                    )}
                </Flex.Row>
              }
              accordionHeight="36px"
              accordionPaddingBottom="4px"
              expanded={selectedItem === item.id}
              removeBoxShadow={true}
            >
              <ScheduledMaintenanceTableRenderComponent
                customerId={customerId as EntityId}
                localityId={localityId as EntityId}
                category={viewMode}
                selectedItemId={item.id}
                onlyDaily={onlyDaily}
                filterIds={filterIds}
              />
            </AccordionCommon>
          ))}
        </>
      ) : (
        <Box className="text-center">
          <EmptyStatusCard
            image={NoMaintenance}
            header={t('no-maintenance-title', { ns: 'maintenance' })}
            content={t('no-maintenance-content', { ns: 'maintenance' })}
            imageWidth="60px"
            headerFontSize="18px"
            contentFontSize="14px"
            contentTopMargin="0px"
            compact={true}
          />
        </Box>
      )}
    </Box>
  )
}

export default React.memo(ScheduledMaintenanceGroupedList)
